import { css, keyframes } from '@emotion/react';
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { isEqual } from 'underscore';

import t from 'react-translate';
import { useAppDispatch } from 'redux/store';

import { WorkdayConfigureRequestParams } from 'redux/schemas/api/institutions';
import { addAlertMessage } from 'redux/actions/alert-messages';
import { AlertMessageType } from 'redux/schemas/app/alert-message';
import { configureWorkday } from 'redux/actions/institutions';

import { doubleSpacing, largeSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import NvIcon from 'shared/components/nv-icon';
import { gray5, warning } from 'styles/global_defaults/colors';
import ClickableContainer from 'components/clickable-container';
import NvTooltip from 'shared/components/nv-tooltip';
import TopicsCSVPicker from './topics-csv-picker';

interface ManageTopicsProps {
  topics: string[];
  onAddTopics: (params: { topics: string[], defaultTopics: string[] }) => void;
  isEditing: boolean;
  defaultTopics?: string[];
}

const slideUp = keyframes`
  from {
    opacity: 0.5;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    opacity: 0.5;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const styles = css`
  .icon-more {
    transform: rotate(90deg);
  }
  .topic-item {
    max-height: ${doubleSpacing}px;
    border-bottom: solid 1px ${gray5};
    transition: all 0.3s ease;
    &.highlight {
      &:hover, &:focus {
        border-bottom: solid 1px ${warning};
      }
    }

    &.animated-up {
      animation: ${slideUp} 0.4s ease-out;
    }
    &.animated-down {
      animation: ${slideDown} 0.4s ease-out;
    }
  }
  .default {
    height: ${standardSpacing}px;
    border-radius: ${largeSpacing}px;
  }
  .nv-file-picker {
    width: fit-content;
  }
`;

const ManageTopics: React.FC<ManageTopicsProps> = ({
  topics: initialTopics,
  defaultTopics: initialDefaultTopics = [],
  onAddTopics,
  isEditing,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [topics, setTopics] = useState<string[]>(initialTopics || []);
  const [defaultTopics, setDefaultTopics] = useState<string[]>(initialDefaultTopics || []);
  const [animatedTopic, setAnimatedTopic] = useState<string | null>(null);
  const [animationDirection, setAnimationDirection] = useState<'up' | 'down' | null>(null);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);

  const institutionId = useSelector((state) => state.app.currentInstitutionId);
  const dispatch = useAppDispatch();

  const markAsDefault = useCallback((topic: string) => {
    const isCurrentlyDefault = defaultTopics.includes(topic);

    setDefaultTopics((prevDefaultTopics) => (isCurrentlyDefault
      ? prevDefaultTopics.filter((defaultTopic) => defaultTopic !== topic)
      : [...prevDefaultTopics, topic]));

    setAnimatedTopic(topic);
    setAnimationDirection(isCurrentlyDefault ? 'down' : 'up');
  }, [defaultTopics]);

  // Sort topics: default topics first, then non-default topics (alphabetically)
  const sortedTopics = useMemo(() => [...topics].sort((a, b) => {
    const aIsDefault = defaultTopics?.includes(a);
    const bIsDefault = defaultTopics?.includes(b);
    if (aIsDefault && !bIsDefault) return -1;
    if (!aIsDefault && bIsDefault) return 1;
    return a.localeCompare(b);
  }), [topics, defaultTopics]);

  const configureTopics = useCallback(async () => {
    setIsRequesting(true);
    const params: WorkdayConfigureRequestParams = {
      institutionId,
      topics,
      defaultTopics,
      ...(isEditing ? {} : { workdayLearnEnabled: true }),
    };

    const result = await dispatch(configureWorkday(params));

    if (result.payload) {
      if (isEditing) {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.TOPICS_UPDATED(),
        }));
      } else {
        dispatch(addAlertMessage({
          type: AlertMessageType.SUCCESS,
          header: t.FORM.SUCCESS_BANG(),
          message: t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.ENABLE_SUCCESS(),
        }));
      }
      onAddTopics({ topics, defaultTopics });
    }
    setIsRequesting(false);
  }, [defaultTopics, dispatch, institutionId, isEditing, onAddTopics, topics]);

  const onAddNewTopics = useCallback((newTopics: string[]) => {
    const uniqueNewTopics = newTopics.filter((topic) => !topics.includes(topic));
    if (uniqueNewTopics.length > 0) {
      setTopics((prevTopics) => [...prevTopics, ...uniqueNewTopics]);
    }
  }, [topics]);

  const handleMouseEnter = useCallback((index: number) => {
    setHoveredIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(null);
  }, []);

  const isSubmitDisabled = useCallback(() => {
    if (isRequesting) return true;
    if (isEditing) {
      return isEqual(topics, initialTopics) && isEqual(defaultTopics, initialDefaultTopics);
    }
    return topics.length === 0;
  }, [isRequesting, isEditing, topics, defaultTopics, initialTopics, initialDefaultTopics]);

  const getSubmitButtonText = useCallback(() => {
    if (isEditing) {
      return isRequesting ? t.FORM.SAVING() : t.FORM.SAVE();
    }
    return isRequesting
      ? t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.ENABLING()
      : t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.ENABLE_WITH_TOPICS();
  }, [isEditing, isRequesting]);

  useEffect(() => {
    if (animatedTopic) {
      const timeoutId = setTimeout(() => {
        setAnimatedTopic(null);
        setAnimationDirection(null);
      }, 400);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [animatedTopic]);

  return (
    <div
      className='d-flex flex-column w-100'
      css={styles}
    >
      {isEditing && (
        <div className='text-body mb-8'>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.TOPICS_DESCRIPTION()}
        </div>
      )}
      <div className='d-flex align-items-center mb-2'>
        <span className='mr-2 label text-gray-2'>
          {t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.TOTAL_TOPICS(topics.length)}
        </span>
        <div className='d-flex'>
          <TopicsCSVPicker
            onConfirmTopics={onAddNewTopics}
          >
            <div className='d-flex align-items-center text-primary'>
              <NvIcon icon='upload' size='smallest' className='mr-1' />
              <span className='label text-primary'>
                {t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.ADD_MORE()}
              </span>
            </div>
          </TopicsCSVPicker>
        </div>
      </div>
      {sortedTopics.map((topic, index) => {
        const itemClass = ['topic-item d-flex align-items-center justify-content-between pl-2 py-2'];
        if (isEditing) {
          itemClass.push('highlight');
        }
        if (animatedTopic === topic && animationDirection === 'up') {
          itemClass.push('animated-up');
        }
        if (animatedTopic === topic && animationDirection === 'down') {
          itemClass.push('animated-down');
        }
        if (index === 0) {
          itemClass.push('border-top');
        }
        const isHovered = index === hoveredIndex;
        const isDefault = defaultTopics.includes(topic);
        const markLabel = isDefault
          ? t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.REMOVE_DEFAULT()
          : t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.MARK_DEFAULT();
        const tooltipText = isDefault
          ? t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.TOOLTIP.REMOVE_AS()
          : t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.TOOLTIP.MARK_AS();
        return (
          <div
            key={topic}
            className={itemClass.join(' ')}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            {...(isEditing && {
              onFocus: () => handleMouseEnter(index),
              onBlur: handleMouseLeave,
              tabIndex: 0,
              role: 'button',
              'aria-label': `${topic} ${markLabel}`,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  markAsDefault(topic);
                }
              },
            })}
          >
            <div className='d-flex align-items-center'>
              {isDefault && (
                <div className='default bg-warning text-small font-weight-bolder text-white px-2 d-flex align-items-center mr-2'>
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.LMS_INTEGRATION.MODAL.WORKDAY_LEARN.DEFAULT()}
                </div>
              )}
              <span className='text-regular font-weight-bolder'>
                {topic}
              </span>
            </div>

            {isHovered && isEditing ? (
              <NvTooltip
                text={tooltipText}
                maxWidth={240}
                className='label text-small font-weight-bolder px-2 py-1'
                enabled
              >
                <ClickableContainer
                  onClick={() => markAsDefault(topic)}
                  className='text-small text-primary'
                  isFocusable={false}
                >
                  {markLabel}
                </ClickableContainer>
              </NvTooltip>
            ) : (
              <NvIcon icon='more' size='smallest' className='text-gray-2' />
            )}
          </div>
        );
      })}
      <div className='d-flex justify-content-center mt-8'>
        <Button
          variant='primary'
          onClick={configureTopics}
          disabled={isSubmitDisabled()}
        >
          {getSubmitButtonText()}
        </Button>
      </div>
    </div>
  );
};

export default ManageTopics;
